if (!customElements.get('pdp-videos')) {
  customElements.define(
    'pdp-videos',
    class PdpVideos extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.pdp-videos__list');
        this.items = Array.from(this.list.children);
        this.dots = Array.from(this.querySelectorAll('.pdp-videos__dot'));

        // The outlined button starts the video inline (same as the thumbnail), then toggles play/pause
        this.querySelectorAll('[data-pdp-videos-play]').forEach((button) => {
          button.addEventListener('click', () => this.togglePlayback(button));
        });

        // Ask each YouTube iframe to report its state once it has loaded (load doesn't bubble, so capture)
        this.addEventListener(
          'load',
          (event) => {
            if (event.target.matches?.('iframe.js-youtube')) this.listenTo(event.target);
          },
          true
        );
        this.onPlayerMessage = this.onPlayerMessage.bind(this);
        window.addEventListener('message', this.onPlayerMessage);

        if (this.dots.length < 2) return;

        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.goTo(index));
        });
        this.list.addEventListener('scroll', this.update.bind(this), { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('message', this.onPlayerMessage);
      }

      togglePlayback(button) {
        const media = button.closest('.pdp-videos__card').querySelector('deferred-media');
        const iframe = media.querySelector('iframe.js-youtube');

        // First click: insert the iframe (autoplay); keep focus on the button so it can toggle
        if (!iframe) {
          this.resetOthers();
          media.loadContent(false);
          const loadedIframe = media.querySelector('iframe.js-youtube');
          if (loadedIframe) this.setPlaying(loadedIframe, true);
          return;
        }

        if (iframe.dataset.playing === 'true') {
          this.sendCommand(iframe, 'pauseVideo');
          this.setPlaying(iframe, false);
        } else {
          window.pauseAllMedia();
          this.resetOthers(iframe);
          this.sendCommand(iframe, 'playVideo');
          this.setPlaying(iframe, true);
        }
      }

      // pauseAllMedia() pauses the other cards, so their buttons go back to "play" right away
      resetOthers(current) {
        this.querySelectorAll('iframe.js-youtube').forEach((frame) => {
          if (frame !== current) this.setPlaying(frame, false);
        });
      }

      // Stores the state on the iframe and switches the card button's icon between play and pause
      setPlaying(iframe, playing) {
        iframe.dataset.playing = playing ? 'true' : 'false';

        const button = iframe.closest('.pdp-videos__card')?.querySelector('[data-pdp-videos-play]');
        if (!button) return;
        button.classList.toggle('pdp-videos__button--playing', playing);
        button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      }

      sendCommand(iframe, func) {
        iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: '' }), '*');
      }

      listenTo(iframe) {
        iframe.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: iframe.title, channel: 'widget' }), '*');
      }

      // Keep data-playing in sync with the player, e.g. after YouTube's own controls or pauseAllMedia()
      onPlayerMessage(event) {
        if (!/^https:\/\/(www\.)?youtube(-nocookie)?\.com$/.test(event.origin)) return;

        const iframe = Array.from(this.querySelectorAll('iframe.js-youtube')).find(
          (frame) => frame.contentWindow === event.source
        );
        if (!iframe) return;

        let data;
        try {
          data = JSON.parse(event.data);
        } catch (error) {
          return;
        }

        let state;
        if (data.event === 'onStateChange') state = data.info;
        if (data.event === 'infoDelivery' && data.info && 'playerState' in data.info) state = data.info.playerState;
        if (typeof state !== 'number') return;

        // 1 = playing, 3 = buffering; everything else (paused, ended, cued) counts as not playing
        this.setPlaying(iframe, state === 1 || state === 3);
      }

      goTo(index) {
        this.list.scrollTo({
          left: this.items[index].offsetLeft - this.items[0].offsetLeft,
          behavior: 'smooth',
        });
      }

      update() {
        const step = this.items[1].offsetLeft - this.items[0].offsetLeft;
        const atEnd = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 2;
        const current = atEnd ? this.items.length - 1 : Math.round(this.list.scrollLeft / step);

        this.dots.forEach((dot, index) => {
          const active = index === current;
          dot.classList.toggle('pdp-videos__dot--active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
